<template>
    <div class="form-group" :class="model.size">
        <div>
            <label class="font-weight-bold">{{ model.label }}</label>
            <i class="far fa-edit float-right text-primary pointer" @click="showEditPrompt"></i>
        </div>
        <div aria-describedby="descriptionHelp">
            <nomenclature-dropdown :fieldName="''"></nomenclature-dropdown>
        </div>
        <small id="descriptionHelp" class="form-text text-muted">{{ isEnVersion ? model.descriptionEn : model.description }}</small>

        <vs-prompt :title="$t('editor.common.editComponentProperties')"
                   :accept-text="$t('buttons.ok')"
                   :cancel-text="$t('buttons.cancel')"
                   :active.sync="activePrompt"
                   :is-valid="validationSuccess"
                   @cancel="cancelPrompt"
                   @close="cancelPrompt"
                   color="primary">
            <div>
                <div v-if="model.locked">
                    <h4 class="text-danger"><i class="fas fa-lock"></i> {{ $t('registers.dynamic.gridHeaders.locked') }}</h4>
                </div>

                <FieldTemplateSelector v-model="model"
                                       :isFormField="isFormField"
                                       :isAuxRegisterField="isAuxRegisterField"
                                       :isEventFieldCode="isEventFieldCode">
                </FieldTemplateSelector>

                <vs-input :label="$t('editor.common.name')" v-model="model.name" />
                <span class="text-danger" v-if="!validName">{{ $t('validation.requiredField') }}</span>

                <div class="row mt-2 mb-2 pl-3">
                    <vs-checkbox v-model="model.isTemplate">{{ $t('editor.common.saveAsTemplate') }}</vs-checkbox>
                </div>

                <vs-input :label="$t('editor.common.nameEN')" v-model="model.nameEn"></vs-input>

                <vs-input :label="$t('editor.common.label')" v-model="model.label" />
                <span class="text-danger" v-if="!validLabel">{{ $t('validation.requiredField') }}</span>
                <vs-input :label="$t('editor.common.labelEn')" v-model="model.labelEn"></vs-input>
                <span class="text-danger" v-if="!validLabelEN">{{ $t('validation.requiredField') }}</span>

                <vs-input :label="$t('editor.common.description')" v-model="model.description" />
                <vs-input :label="$t('editor.common.descriptionEn')" v-model="model.descriptionEn" />

                <size-dropdown v-model="model.size"></size-dropdown>

                <div class="card mt-2 mb-2">
                    <div class="card-body">
                        <div class="mb-2">
                            <vs-select v-model="model.dropdownType"
                                       :label="$t('editor.dropdown.dropdownType')"
                                       width="100%"
                                       @change="dropdownTypeChanged"
                                       :placeholder="$t('choose')">
                                <vs-select-item :key="'dropdownType_' + item.value"
                                                :value="item.value"
                                                :text="item.text"
                                                v-for="item in dropdownTypeItems" />
                            </vs-select>

                            <div class="float-left mt-2 mb-2" v-if="model.dropdownType === dropdownType.Nomenclature">
                                <vs-checkbox v-model="model.dropdownIsCity">{{ $t('editor.dropdown.isCity') }}</vs-checkbox>
                            </div>

                            <div class="text-danger" v-if="!validDropdownType">{{ $t('validation.requiredField') }}</div>
                        </div>

                        <div v-if="model.dropdownType === dropdownType.Nomenclature">
                            <div class="loader" v-if="loading"></div>
                            <div v-else>
                                <div v-if="!model.dropdownIsCity" class="mb-2">
                                    <vs-select v-model="model.dropdownId"
                                               :label="$t('editor.dropdown.chooseNomenclature')"
                                               width="100%"
                                               :placeholder="$t('choose')">
                                        <vs-select-item :key="'nom_' + item.id"
                                                        :value="item.id"
                                                        :text="item.text"
                                                        v-for="item in nomenclatures" />
                                    </vs-select>

                                    <div v-if="model.dropdownId">
                                        <vs-select v-model="dropdownDefaultValue"
                                                   :label="$t('editor.common.defaultValue')"
                                                   width="100%"
                                                   :placeholder="$t('choose')">
                                            <vs-select-item :key="'default_empty'" :value="''" :text="$t('choose')" />
                                            <vs-select-item :key="'nomItem_' + item.id"
                                                            :value="item.id"
                                                            :text="item.text"
                                                            v-for="item in nomenclatureItems" />
                                        </vs-select>
                                    </div>
                                    <!-- TODO: Multiselect -->
                                </div>
                            </div>
                        </div>

                        <div v-if="model.dropdownType === dropdownType.Register">
                            <div class="loader" v-if="loading"></div>
                            <div v-else class="mb-2">
                                <vs-select v-model="model.dropdownId"
                                           :label="$t('editor.dropdown.chooseRegister')"
                                           @change="selectedDropdownChanged($event)"
                                           width="100%"
                                           :placeholder="$t('choose')">
                                    <vs-select-item :key="'staticReg_' + item.id"
                                                    :value="item.id"
                                                    :text="item.text"
                                                    v-for="item in staticRegisters" />
                                </vs-select>

                                <vs-select v-if="model.dropdownId"
                                           ref="staticDisplayMemberSelect"
                                           :key="'staticDisplayMember_' + model.dropdownId"
                                           v-model="model.dropdownDisplayMember"
                                           :label="$t('editor.dropdown.chooseRegisterDisplayName')"
                                           width="100%"
                                           :placeholder="$t('choose')">
                                    <vs-select-item :key="'staticRegDisplay_' + item.id"
                                                    :value="item.id"
                                                    :text="item.text"
                                                    v-for="item in staticRegisterDisplayMembers" />
                                </vs-select>

                            </div>
                        </div>

                        <div v-if="model.dropdownType === dropdownType.DynamicRegister">
                            <div class="loading" v-if="loading"></div>
                            <div v-else class="mb-2">
                                <vs-select v-model="model.dropdownId"
                                           :label="$t('editor.dropdown.chooseRegister')"
                                           @change="selectedDropdownChanged($event)"
                                           width="100%"
                                           :placeholder="$t('choose')">
                                    <vs-select-item :key="'dynReg_' + item.id"
                                                    :value="item.id"
                                                    :text="item.text"
                                                    v-for="item in dynamicRegisters" />
                                </vs-select>

                                <vs-select v-if="model.dropdownId"
                                           ref="dynamicDisplayMemberSelect"
                                           :key="'dynDisplayMember_' + model.dropdownId"
                                           v-model="model.dropdownDisplayMember"
                                           :label="$t('editor.dropdown.chooseRegisterDisplayName')"
                                           width="100%"
                                           :placeholder="$t('choose')">
                                    <vs-select-item :key="'dynRegDisplay_' + item.id"
                                                    :value="item.id"
                                                    :text="item.text"
                                                    v-for="item in dynamicRegisterDisplayMembers" />
                                </vs-select>
                            </div>
                        </div>

                        <div class="text-danger" v-if="!validDropdownValue && model.dropdownType && !model.dropdownIsCity">{{ $t('validation.requiredField')  }}</div>
                    </div>
                </div>

                <div class="float-left mt-2">
                    <vs-checkbox v-model="model.isRequired">{{ $t('validation.requiredField') }}</vs-checkbox>
                </div>
                <div class="float-left mt-2">
                    <vs-checkbox v-model="model.isMultiDropdown">{{ $t('editor.dropdown.isMultiDropdown') }}</vs-checkbox>
                </div>

                <div class="clearfix"></div>
                <hr />
                <div class="float-left mt-2">
                    <vs-checkbox v-model="model.showInDescription">{{ $t('editor.common.showInDescription') }}</vs-checkbox>
                </div>

                <div class="clearfix"></div>
            </div>
        </vs-prompt>
    </div>
</template>

<script>
    import { EditorComponentModel } from '@/models/models';
    import NomenclatureDropdown from '../dropdown/NomenclatureDropdown';
    import SizeDropdown from '@/components/editor/SizeDropdown';
    import FieldTemplateSelector from '@/components/editor/FieldTemplateSelector'
    import { DropdownType, Culture } from '@/models/enums';

    export default {
        components: {
            NomenclatureDropdown,
            SizeDropdown,
            FieldTemplateSelector
        },
        props: {
            value: String,
            model: {
                type: EditorComponentModel
            },
            isFormField: {
                type: Boolean,
                default: false
            },
            isAuxRegisterField: {
                type: Boolean,
                required: false
            },
            isEventFieldCode: {
                type: Boolean,
                required: false
            },
        },
        data() {
            return {
                loading: false,
                activePrompt: false,
                originalModel: new EditorComponentModel(),
                dropdownType: DropdownType,
                dropdownTypeItems: [{
                    text: this.$t('editor.dropdown.nomenclature'),
                    value: DropdownType.Nomenclature
                }, {
                    text: this.$t('editor.dropdown.register'),
                    value: DropdownType.Register
                }, {
                    text: this.$t('editor.dropdown.dynamicRegister'),
                    value: DropdownType.DynamicRegister
                }],
                dropdownDefaultValue: '',
                nomenclatures: [],
                nomenclatureItems: [],
                staticRegisters: [],
                staticRegisterDisplayMembers: [],
                dynamicRegisters: [],
                dynamicRegisterDisplayMembers: [],
            }
        },
        methods: {
            init() {
                if (this.model.dropdownType === this.dropdownType.Nomenclature) {
                    this.loadNomenclatures();
                } else if (this.model.dropdownType === this.dropdownType.Register) {
                    this.loadStaticRegisters();
                } else if (this.model.dropdownType === this.dropdownType.DynamicRegister) {
                    this.loadDynamicRegisters();
                }
            },
            loadItems() {
                const url = `/api/search/GetNomenclatureItems?type=${this.type}`;
                this.$http.get(url).then(result => {
                    this.items = result.data;
                });
            },
            showEditPrompt(event) {
                this.originalModel = new EditorComponentModel(this.model);
                this.activePrompt = true;
            },
            cancelPrompt() {
                this.changeModelToOriginalModel();
            },
            changeModelToOriginalModel() {
                this.model.name = this.originalModel.name;
                this.model.nameEn = this.originalModel.nameEn;
                this.model.label = this.originalModel.label;
                this.model.labelEn = this.originalModel.labelEn;
                this.model.size = this.originalModel.size;
                this.model.dropdownType = this.originalModel.dropdownType;
                this.model.dropdownId = this.originalModel.dropdownId;
                this.model.isRequired = this.originalModel.isRequired;
                this.model.isReadOnly = this.originalModel.isReadOnly;
                this.model.showInDescription = this.originalModel.showInDescription;
            },
            loadNomenclatures() {
                this.loading = true;
                this.$http.get('/api/Dropdown/GetParentNomenclatures').then(result => {
                    this.nomenclatures = result.data;
                    this.loading = false;
                    // loadNomenclatureItems се извиква от watcher-а за model.dropdownId
                }).catch(error => {
                    this.loading = false;
                    this.showError(this.$t('editor.errors.loadNomenclaturesError'));
                });
            },
            loadNomenclatureItems() {
                if (this.model.dropdownId) {
                    this.$http.get(`/api/Search/GetNomenclatureItemsById?id=${this.model.dropdownId}`).then(result => {
                        this.nomenclatureItems = result.data;

                        if (this.model.isMultiDropdown) {
                            this.dropdownDefaultValue = this.model.textDefaultValue; // TODO: parse |
                        } else {
                            this.dropdownDefaultValue = this.model.textDefaultValue; // this.model.intDefaultValue;
                        }

                        this.loading = false;
                    }).catch(error => {
                        this.loading = false;
                        this.showError(this.$t('editor.errors.loadNomenclaturesError'));
                    });
                }
            },
            loadStaticRegisters() {
                this.loading = true;
                this.$http.get('/api/dropdown/GetStaticRegisters').then(result => {
                    this.staticRegisters = result.data;
                    this.loading = false;
                }).catch(error => {
                    this.loading = false;
                    this.showError(this.$t('editor.errors.loadStaticRegistersError'));
                });
            },
            loadStaticRegisterDisplayMembers() {
                this.loading = true;
                this.$http.get(`/api/dropdown/GetDisplayMembersByStaticRegister/${this.getRegisterName(this.model.dropdownId)}`)
                    .then(result => {
                        this.staticRegisterDisplayMembers = result.data;
                        this.loading = false;
                    }).catch(error => {
                        this.loading = false;
                        this.showError(this.$t('editor.errors.loadDisplayMembersError'));
                    });
            },
            loadDynamicRegisters() {
                this.loading = true;
                this.$http.get('/api/dropdown/GetDynamicRegisters').then(result => {
                    this.dynamicRegisters = result.data;
                    this.loading = false;
                }).catch(error => {
                    this.loading = false;
                    this.showError(this.$t('editor.errors.loadDynamicRegistersError'));
                });
            },
            loadDynamicRegisterDisplayMembers() {
                this.loading = true;
                this.$http.get(`/api/dropdown/GetDisplayMemberByDynamicRegister/${this.model.dropdownId}`)
                    .then(result => {
                        this.dynamicRegisterDisplayMembers = result.data;
                        this.loading = false;
                    }).catch(error => {
                        this.loading = false;
                        this.showError(this.$t('editor.errors.loadDisplayMembersError'));
                    });
            },
            dropdownTypeChanged(event) {
                this.$set(this.model, 'dropdownId', null);
                this.$set(this.model, 'dropdownDisplayMember', null);

                if (this.model.dropdownType === this.dropdownType.Nomenclature) {
                    this.loadNomenclatures();
                } else if (this.model.dropdownType === this.dropdownType.Register) {
                    this.loadStaticRegisters();
                } else if (this.model.dropdownType === this.dropdownType.DynamicRegister) {
                    this.loadDynamicRegisters();
                }
            },
            selectedDropdownChanged(event, value) {
                // Изчистване на displayMember при смяна на регистъра
                this.model.dropdownDisplayMember = '';

                // Ресет на vs-select чрез ref ако съществува
                this.$nextTick(() => {
                    if (this.$refs.staticDisplayMemberSelect) {
                        this.$refs.staticDisplayMemberSelect.value = '';
                    }
                    if (this.$refs.dynamicDisplayMemberSelect) {
                        this.$refs.dynamicDisplayMemberSelect.value = '';
                    }
                });

                if (this.model.dropdownType === this.dropdownType.Register) {
                    this.loadStaticRegisterDisplayMembers();
                } else if (this.model.dropdownType === this.dropdownType.DynamicRegister) {
                    this.loadDynamicRegisterDisplayMembers();
                }
            },
            getRegisterName(id) {
                for (let i = 0; i < this.staticRegisters.length; i++) {
                    if (this.staticRegisters[i].id === id) {
                        return this.staticRegisters[i].text;
                    }
                }
            },
            showError(message) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: message,
                    position: 'top-center',
                    color: 'danger'
                });
            }
        },
        computed: {
            validName() {
                return this.model.name.length > 0;
            },
            validLabel() {
                return this.model.label.length > 0;
            },
            validLabelEN() {
                return this.model.labelEn.length > 0;
            },
            validDropdownType() {
                return this.model.dropdownType === this.dropdownType.Nomenclature
                    || this.model.dropdownType === this.dropdownType.Register
                    || this.model.dropdownType === this.dropdownType.DynamicRegister;
            },
            validDropdownValue() {
                return parseInt(this.model.dropdownId) > 0;
            },
            validationSuccess() {
                if (this.model.locked) {
                    // Locked for edition
                    return false;
                }

                if (this.model.dropdownType === this.dropdownType.Nomenclature) {
                    if (!this.model.dropdownIsCity && !this.validDropdownValue) {
                        return false;
                    }
                }

                return this.validName && this.validLabel && this.validLabelEN && this.validDropdownType;
            },
            isEnVersion() {
                return this.$store.getters.currentCulture === Culture.EN;
            },
        },
        watch: {
            'model.dropdownId': {
                handler: function(newVal, oldVal) {
                    // Изчистване при смяна на dropdownId
                    if (oldVal && newVal !== oldVal) {
                        // Изчистване на displayMember за Register/DynamicRegister
                        this.model.dropdownDisplayMember = '';
                        // Изчистване на defaultValue за Nomenclature
                        this.dropdownDefaultValue = '';
                    }

                    if (newVal && this.model.dropdownType === this.dropdownType.Nomenclature && !this.model.dropdownIsCity) {
                        this.loadNomenclatureItems();
                    }

                    // Зареждане на display members за Register/DynamicRegister
                    if (newVal && this.model.dropdownType === this.dropdownType.Register) {
                        this.loadStaticRegisterDisplayMembers();
                    } else if (newVal && this.model.dropdownType === this.dropdownType.DynamicRegister) {
                        this.loadDynamicRegisterDisplayMembers();
                    }
                },
                immediate: true
            },
            dropdownDefaultValue: function(newVal, oldVal) {
                if (newVal && newVal !== 0) {
                    if (this.model.isMultiDropdown) {
                        // TODO:
                        this.model.textDefaultValue = newVal;
                    } else {
                        // Because does not save intDefaultValue...
                        this.model.textDefaultValue = newVal;
                    }
                } else {
                    this.model.textDefaultValue = '';
                }
            }
        },
        mounted() {
            this.init();
            this.selectedDropdownChanged();
        }
    }
</script>

<style scoped>
    .vs-input {
        width: 100%;
    }

    ::v-deep {
        .vs-dialog {
            min-width: 800px !important;
            height: auto;
        }
    }
</style>
